import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useReactiveVar } from '@apollo/client';
import {
  Modal,
  Button,
  Checkbox,
  Slider,
  Toggle,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@statsbomb/kitbag-components';
import classNames from 'classnames';
import {
  uiState,
  cf_PlayType,
  cf_PlayTypeEnabled,
  cf_YardsGained,
  cf_YardsGainedEnabled,
  cf_PassAirYardsEnabled,
  cf_PassAirYards,
  cf_QBDropDepth,
  cf_QBDropDepthEnabled,
  cf_QBTimeToThrow,
  cf_QBPocketLocation,
  cf_QBTimeToThrowEnabled,
  cf_QBPocketLocationEnabled,
} from '../../apollo';
import { returnReadableYardsLabel } from '../../utils/helpers/general';
import { normalPlayTypes, qbPocketLocation } from '../../utils/constants/api';
import {
  FilterDynamicValue,
  FilterRoundel,
  FiltersHR,
  FiltersTrack,
  ModalWidth,
  TriggerButtonContainer,
} from './ContextFilters.styles';
import { enableFilter, getTimeToLabel } from './Filters.helper';
import { useIsEligiblePage } from './ContextFilters.hooks';
import { FormField, FormGroupEqual } from '../../styles/global.styles';
import {
  depthOfTargetLabels,
  qbDropDepthLabels,
  qbTimeToThrowLabels,
  yardsGainedLabels,
} from './TypeFilter.constants';

const TypeFilter = ({ handleEnabledToggle }) => {
  // modal open/close
  const [st_ModalOpen, setModalOpen] = useState(false);
  // context filters in global state
  const cf_PlayTypeRV = useReactiveVar(cf_PlayType);
  const cf_PlayTypeEnabledRV = useReactiveVar(cf_PlayTypeEnabled);
  const cf_YardsGainedRV = useReactiveVar(cf_YardsGained);
  const cf_YardsGainedEnabledRV = useReactiveVar(cf_YardsGainedEnabled);
  const cf_QBDropDepthRV = useReactiveVar(cf_QBDropDepth);
  const cf_QBDropDepthEnabledRV = useReactiveVar(cf_QBDropDepthEnabled);
  const cf_QBTimeToThrowRV = useReactiveVar(cf_QBTimeToThrow);
  const cf_QBTimeToThrowEnabledRV = useReactiveVar(cf_QBTimeToThrowEnabled);
  const cf_QBPocketLocationRV = useReactiveVar(cf_QBPocketLocation);
  const cf_QBPocketLocationEnabledRV = useReactiveVar(
    cf_QBPocketLocationEnabled
  );
  const cf_PassAirYardsRV = useReactiveVar(cf_PassAirYards);
  const cf_PassAirYardsEnabledRV = useReactiveVar(cf_PassAirYardsEnabled);

  // context filters in local state
  const [st_PlayType, setPlayType] = useState(cf_PlayTypeRV);
  const [st_YardsGained, setYardsGained] = useState(cf_YardsGainedRV);
  const [st_QBDropDepth, setQBDropDepth] = useState(cf_QBDropDepthRV);
  const [st_QBTimeToThrow, setQBTimeToThrow] = useState(cf_QBTimeToThrowRV);
  const [st_QBPocketLocation, setQBPocketLocation] = useState(
    cf_QBPocketLocationRV
  );
  const [st_PassAirYards, setPassAirYards] = useState(cf_PassAirYardsRV);

  const handlePlayType = (isTypeChecked, index) => {
    const playType = [...st_PlayType];
    playType[index] = isTypeChecked;

    // if pass play is disabled, disable drop depth and time to throw
    if (index === 0 && !isTypeChecked) {
      if (cf_QBDropDepthEnabledRV) {
        cf_QBDropDepthEnabled(false);
      }
      if (cf_QBTimeToThrowEnabledRV) {
        cf_QBTimeToThrowEnabled(false);
      }
    }
    enableFilter(cf_PlayTypeEnabledRV, cf_PlayTypeEnabled, 'playType');
    setPlayType([...playType]);
  };

  const handlePlayEnabledToggle = (enabled, rv, property, update) => {
    // if pass play is disabled and the user enables playTypes THEN
    // disable enabled toggles for QB drop and QBTimeToThrow
    if (st_PlayType[0] === false && enabled) {
      handleEnabledToggle(false, cf_QBDropDepthEnabled, 'qbDropDepth', {
        enabled: false,
      });
      handleEnabledToggle(false, cf_QBTimeToThrowEnabled, 'qbTimeToThrow', {
        enabled: false,
      });
    }

    handleEnabledToggle(enabled, rv, property, update);
  };

  const handleQBPocketLocation = (isLocationChecked, index) => {
    const pocketLocation = [...st_QBPocketLocation];
    pocketLocation[index] = isLocationChecked;
    setQBPocketLocation([...pocketLocation]);
    enableFilter(
      cf_QBPocketLocationEnabledRV,
      cf_QBPocketLocationEnabled,
      'qbPocketLocation'
    );
  };

  const handleYardsGained = (evt, sliderValues) => {
    setYardsGained([...sliderValues]);
  };

  const handleQBDropDepth = (evt, sliderValues) => {
    setQBDropDepth([...sliderValues]);
  };

  const handleQBTimeToThrow = (evt, sliderValues) => {
    setQBTimeToThrow([...sliderValues]);
  };

  const handlePassAirYards = (evt, sliderValues) => {
    setPassAirYards([...sliderValues]);
  };

  const handleReset = () => {
    setPlayType([true, true, false, false]);
    cf_PlayTypeEnabled(false);
    setQBDropDepth([0, 16]);
    cf_QBDropDepthEnabled(false);
    setQBTimeToThrow([0, 5.1]);
    cf_QBTimeToThrowEnabled(false);
    setQBPocketLocation([
      ...new Array(Object.keys(qbPocketLocation).length).fill(true),
    ]);
    cf_QBPocketLocation(false);
    setYardsGained([-10, 25]);
    cf_YardsGainedEnabled(false);
    setPassAirYards([-5, 40]);
    cf_PassAirYardsEnabled(false);

    uiState().contextFilters.playType.enabled = true;
    uiState().contextFilters.playType.value = [true, true, false, false];
    uiState().contextFilters.qbDropDepth.enabled = false;
    uiState().contextFilters.qbDropDepth.value = [0, 16];
    uiState().contextFilters.qbTimeToThrow.enabled = false;
    uiState().contextFilters.qbTimeToThrow.value = [0, 5.1];
    uiState().contextFilters.qbPocketLocation.enabled = false;
    uiState().contextFilters.qbPocketLocation.value = [
      ...new Array(Object.keys(qbPocketLocation).length).fill(true),
    ];
    uiState().contextFilters.yardsGained.value = [-10, 25];
    uiState().contextFilters.yardsGained.enabled = false;
    uiState().contextFilters.passAirYards.value = [-5, 40];
    uiState().contextFilters.passAirYards.enabled = false;

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
  };

  const handleModalClose = () => {
    const { contextFilters } = uiState();
    const {
      playType,
      yardsGained,
      passAirYards,
      qbDropDepth,
      qbTimeToThrow,
      qbPocketLocation: qbPocketLocationContext,
    } = contextFilters;

    cf_PlayType([...st_PlayType]);
    playType.value = [...st_PlayType];
    cf_PlayTypeEnabled(cf_PlayTypeEnabledRV);

    cf_QBDropDepth([...st_QBDropDepth]);
    qbDropDepth.value = [...st_QBDropDepth];
    cf_QBDropDepthEnabled(cf_QBDropDepthEnabledRV);

    cf_QBTimeToThrow([...st_QBTimeToThrow]);
    qbTimeToThrow.value = [...st_QBTimeToThrow];
    cf_QBTimeToThrowEnabled(cf_QBTimeToThrowEnabledRV);

    cf_QBPocketLocation([...st_QBPocketLocation]);
    qbPocketLocationContext.value = [...st_QBPocketLocation];
    cf_QBPocketLocationEnabled(cf_QBPocketLocationEnabledRV);

    cf_YardsGained([...st_YardsGained]);
    yardsGained.value = [...st_YardsGained];
    cf_YardsGainedEnabled(cf_YardsGainedEnabledRV);

    cf_PassAirYards([...st_PassAirYards]);
    passAirYards.value = [...st_PassAirYards];
    cf_PassAirYardsEnabled(cf_PassAirYardsEnabledRV);

    sessionStorage.setItem('uiStateLocal', JSON.stringify(uiState()));
    setModalOpen(false);
  };

  const filterCount = [
    cf_PlayTypeEnabledRV,
    cf_YardsGainedEnabledRV,
    cf_PassAirYardsEnabledRV,
    cf_QBDropDepthEnabledRV,
    cf_QBTimeToThrowEnabledRV,
    cf_QBPocketLocationEnabledRV,
  ].filter(Boolean).length;
  const isEligiblePage = useIsEligiblePage('PLAY');
  const isEnabled = filterCount > 0 && isEligiblePage;

  return (
    <>
      <TriggerButtonContainer>
        <Button
          variant={isEnabled ? 'primary' : 'secondary'}
          size="small"
          isDisabled={!isEligiblePage}
          onClick={() => setModalOpen(true)}
        >
          Play
        </Button>
        {isEnabled && <FilterRoundel>{filterCount}</FilterRoundel>}
      </TriggerButtonContainer>

      <Modal onCancel={handleModalClose} isOpen={st_ModalOpen}>
        <ModalWidth $minWidth="570px">
          <ModalHeader>Play</ModalHeader>
          <ModalBody>
            <FormField $marginBottom="0.5rem">
              <Toggle
                id="play-type-cf-toggle"
                label="Play Type"
                onChange={() =>
                  handlePlayEnabledToggle(
                    !cf_PlayTypeEnabledRV,
                    cf_PlayTypeEnabled,
                    'playType',
                    {
                      enabled: !cf_PlayTypeEnabledRV,
                    }
                  )
                }
                checked={cf_PlayTypeEnabledRV}
                size="small"
                data-testid="play-type-toggle"
              />
            </FormField>
            <FormGroupEqual>
              {st_PlayType &&
                Object.values(normalPlayTypes).map((item, index) => (
                  <div key={`normal-play-types-${item}`}>
                    <Checkbox
                      id={`normal-play-types-${item}`}
                      label={item}
                      onChange={() =>
                        handlePlayType(!st_PlayType[index], index)
                      }
                      value={String(st_PlayType[index])}
                      checked={st_PlayType[index]}
                      size="small"
                      disabled={!cf_PlayTypeEnabledRV}
                      data-testid={`normal-play-types-${item}`}
                    />
                  </div>
                ))}
            </FormGroupEqual>

            <FiltersHR $margin="1.25rem 1rem 0.75rem" />

            <FormField $marginBottom="0.5rem">
              <Toggle
                id="yards-gained-cf-toggle"
                label="Yards Gained"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_YardsGainedEnabledRV,
                    cf_YardsGainedEnabled,
                    'yardsGained',
                    {
                      enabled: !cf_YardsGainedEnabledRV,
                    }
                  )
                }
                checked={cf_YardsGainedEnabledRV}
                size="small"
                data-testid="yards-gained-toggle"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_YardsGainedEnabledRV,
                })}
                data-testid="yards-gained-label"
              >
                {st_YardsGained &&
                  returnReadableYardsLabel(st_YardsGained, -11, 26)}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0">
              <Slider
                id="yards-gained-context-filter-slider"
                labels={yardsGainedLabels}
                min={-11}
                max={26}
                onChange={handleYardsGained}
                isDisabled={!cf_YardsGainedEnabledRV}
                isPushable
                value={st_YardsGained}
                version={2}
              />
              <FiltersTrack
                className="extreme"
                $opacity={cf_YardsGainedEnabledRV ? 1 : 0.35}
              >
                <div>&lsaquo;</div>
                <div>&rsaquo;</div>
              </FiltersTrack>
            </FormField>

            <FiltersHR $margin="0.5rem 1rem 0.75rem" />

            <FormField $marginBottom="0.5rem">
              <Toggle
                id="qb-drop-depth-cf-toggle"
                label="QB Drop Depth"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_QBDropDepthEnabledRV,
                    cf_QBDropDepthEnabled,
                    'qbDropDepth',
                    {
                      enabled: !cf_QBDropDepthEnabledRV,
                    }
                  )
                }
                checked={cf_QBDropDepthEnabledRV}
                size="small"
                data-testid="qb-drop-depth-toggle"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_QBDropDepthEnabledRV,
                })}
              >
                {st_QBDropDepth &&
                  returnReadableYardsLabel(st_QBDropDepth, 0, 16)}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0.5rem">
              <Slider
                id="qb-drop-depth-context-filter-slider"
                labels={qbDropDepthLabels}
                min={0}
                max={16}
                onChange={handleQBDropDepth}
                isDisabled={!cf_QBDropDepthEnabledRV}
                isPushable
                value={st_QBDropDepth}
                version={2}
              />
              <FiltersTrack
                className="extreme"
                $opacity={cf_QBDropDepthEnabledRV ? 1 : 0.35}
              >
                <div />
                <div>&rsaquo;</div>
              </FiltersTrack>
            </FormField>

            <FiltersHR $margin="0.5rem 1rem 0.75rem" />

            <FormField $marginBottom="0.5rem">
              <Toggle
                id="qb-ttt-cf-toggle"
                label="QB Time to Throw"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_QBTimeToThrowEnabledRV,
                    cf_QBTimeToThrowEnabled,
                    'qbTimeToThrow',
                    {
                      enabled: !cf_QBTimeToThrowEnabledRV,
                    }
                  )
                }
                checked={cf_QBTimeToThrowEnabledRV}
                size="small"
                data-testid="qb-time-to-throw-toggle"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_QBTimeToThrowEnabledRV,
                })}
              >
                {st_QBTimeToThrow && getTimeToLabel(st_QBTimeToThrow, [0, 5.1])}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0.5rem">
              <Slider
                id="qb-time-to-throw-context-filter-slider"
                labels={qbTimeToThrowLabels}
                min={0}
                max={5.1}
                step={0.1}
                onChange={handleQBTimeToThrow}
                isDisabled={!cf_QBTimeToThrowEnabledRV}
                isPushable
                value={st_QBTimeToThrow}
                version={2}
              />
              <FiltersTrack
                className="extreme"
                $opacity={cf_QBTimeToThrowEnabledRV ? 1 : 0.35}
              >
                <div />
                <div>&rsaquo;</div>
              </FiltersTrack>
            </FormField>

            <FiltersHR $margin="0.5rem 1rem 0.75rem" />

            <FormField $marginBottom="0.5rem">
              <Toggle
                id="qb-pocket-location-cf-toggle"
                label="QB Pocket Location"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_QBPocketLocationEnabledRV,
                    cf_QBPocketLocationEnabled,
                    'qbPocketLocation',
                    {
                      enabled: !cf_QBPocketLocationEnabledRV,
                    }
                  )
                }
                checked={cf_QBPocketLocationEnabledRV}
                size="small"
                data-testid="qb-pocket-location-toggle"
              />
            </FormField>
            <FormGroupEqual>
              {st_QBPocketLocation &&
                Object.values(qbPocketLocation).map((item, index) => (
                  <div key={`qb-pocket-location-${item}`}>
                    <Checkbox
                      id={`qb-pocket-location-${item}-checkbox`}
                      label={item}
                      onChange={() =>
                        handleQBPocketLocation(
                          !st_QBPocketLocation[index],
                          index
                        )
                      }
                      value={String(st_QBPocketLocation[index])}
                      checked={st_QBPocketLocation[index]}
                      size="small"
                      disabled={!cf_QBPocketLocationEnabledRV}
                    />
                  </div>
                ))}
            </FormGroupEqual>

            <FiltersHR $margin="1.25rem 1rem 0.75rem" />

            <FormField $marginBottom="0.5rem">
              <Toggle
                id="depth-of-target-cf-toggle"
                label="Depth of Target"
                onChange={() =>
                  handleEnabledToggle(
                    !cf_PassAirYardsEnabledRV,
                    cf_PassAirYardsEnabled,
                    'passAirYards',
                    {
                      enabled: !cf_PassAirYardsEnabledRV,
                    }
                  )
                }
                checked={cf_PassAirYardsEnabledRV}
                size="small"
                data-testid="depth-of-target-toggle"
              />
              <FilterDynamicValue
                className={classNames({
                  grey: !cf_PassAirYardsEnabledRV,
                })}
                data-testid="depth-of-target-label"
              >
                {st_PassAirYards &&
                  returnReadableYardsLabel(st_PassAirYards, -6, 41)}
              </FilterDynamicValue>
            </FormField>
            <FormField $marginBottom="0">
              <Slider
                id="depth-of-target-context-filter-slider"
                labels={depthOfTargetLabels}
                min={-6}
                max={41}
                onChange={handlePassAirYards}
                isDisabled={!cf_PassAirYardsEnabledRV}
                isPushable
                value={st_PassAirYards}
                version={2}
              />
              <FiltersTrack
                className="extreme"
                $opacity={cf_PassAirYardsEnabledRV ? 1 : 0.35}
              >
                <div>&lsaquo;</div>
                <div>&rsaquo;</div>
              </FiltersTrack>
            </FormField>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={handleReset}>
              Reset
            </Button>
            <Button onClick={handleModalClose}>Save</Button>
          </ModalFooter>
        </ModalWidth>
      </Modal>
    </>
  );
};

TypeFilter.propTypes = {
  handleEnabledToggle: PropTypes.func.isRequired,
};

export default TypeFilter;
